import { ConnectionConfig } from "@/types";
import fetchNeighbors from "./fetchNeighbors";
import fetchNeighborsCount from "./fetchNeighborsCount";
import fetchSchema from "./fetchSchema";
import fetchVertexTypeCounts from "./fetchVertexTypeCounts";
import keywordSearch from "./keywordSearch";
import { GraphSummary, GremlinFetch } from "./types";
import { v4 } from "uuid";
import { Explorer, ExplorerRequestOptions } from "../useGEFetchTypes";
import { logger } from "@/utils";
import { createLoggerFromConnection } from "@/core/connector";
import { FeatureFlags } from "@/core";
import { vertexDetails } from "./vertexDetails";
import { edgeDetails } from "./edgeDetails";
import axios from "axios";
import { RCH_CONFIG_DATA } from "@/utils/const";
import { Config } from "@/context/config-context";
import { User } from "oidc-client-ts";

function getUser(authority?: string, clientId?: string) {
  const oidcStorage = window.localStorage.getItem(
    `oidc.user:${authority}:${clientId}`,
  );
  if (!oidcStorage) {
    return null;
  }
  return User.fromStorageString(oidcStorage);
}

const getToken = () => {
  const configData:Config = JSON.parse(window.localStorage.getItem(RCH_CONFIG_DATA) || '{}');
  const oidcProvider = configData.aws_oidc_provider;
  const oidcClientId = configData.aws_oidc_client_id;
  const user = getUser(oidcProvider, oidcClientId);
  const token = user?.id_token ?? '';
  return token;
};

function _gremlinFetch(
  connection: ConnectionConfig,
  featureFlags: FeatureFlags,
  options?: ExplorerRequestOptions
): GremlinFetch {
  return async (queryTemplate: string) => {
    logger.debug(queryTemplate);
    // const body = JSON.stringify({ query: queryTemplate });
    const headers: HeadersInit = {
      "Content-Type": "application/json",
      Accept: "application/vnd.gremlin-v3.0+json",
      Authorization: `Bearer ${getToken()}`,
    };
    if (options?.queryId && connection.proxyConnection === true) {
      headers.queryId = options.queryId;
    }
    const response = await axios.post(`${connection.url}/gremlin`, {
      gremlin: queryTemplate,
    }, {
      headers,
    });

    return response.data;

    // TODO: Magic Comments Start
    // return fetchDatabaseRequest(
    //   connection,
    //   featureFlags,
    //   `${connection.url}/gremlin`,
    //   {
    //     method: "POST",
    //     headers,
    //     body,
    //     ...options,
    //   }
    // );
  };
}

async function fetchSummary(
  connection: ConnectionConfig,
  featureFlags: FeatureFlags,
  options?: RequestInit
) {
  try {
    // TODO Magic comments start
    // const response = await fetchDatabaseRequest(
    //   connection,
    //   featureFlags,
    //   `${connection.url}/pg/statistics/summary?mode=detailed`,
    //   {
    //     method: "GET", 
    //     ...options,
    //   }
    // );
    const headers: HeadersInit = {
      "Content-Type": "application/json",
      Accept: "application/vnd.gremlin-v3.0+json",
      Authorization: `Bearer ${getToken()}`,
    };
    const response = await axios.get(`${connection.url}/graph-summary?mode=detailed`, {
      headers,
    });
    return response.data.payload.graphSummary as GraphSummary;
  } catch (error) {
    logger.error(
      "[Gremlin Explorer] Failed to gather summary statistics",
      error
    );
  }
}

export function createGremlinExplorer(
  connection: ConnectionConfig,
  featureFlags: FeatureFlags
): Explorer {
  const remoteLogger = createLoggerFromConnection(connection);
  return {
    connection: connection,
    async fetchSchema(options) {
      remoteLogger.info("[Gremlin Explorer] Fetching schema...");
      const summary = await fetchSummary(connection, featureFlags, options);
      return fetchSchema(
        _gremlinFetch(connection, featureFlags, options),
        remoteLogger,
        summary
      );
    },
    async fetchVertexCountsByType(req, options) {
      remoteLogger.info("[Gremlin Explorer] Fetching vertex counts by type...");
      return fetchVertexTypeCounts(
        _gremlinFetch(connection, featureFlags, options),
        req
      );
    },
    async fetchNeighbors(req, options) {
      remoteLogger.info("[Gremlin Explorer] Fetching neighbors...");
      return fetchNeighbors(
        _gremlinFetch(connection, featureFlags, options),
        req
      );
    },
    async fetchNeighborsCount(req, options) {
      remoteLogger.info("[Gremlin Explorer] Fetching neighbors count...");
      return fetchNeighborsCount(
        _gremlinFetch(connection, featureFlags, options),
        req
      );
    },
    async keywordSearch(req, options) {
      options ??= {};
      options.queryId = v4();

      remoteLogger.info("[Gremlin Explorer] Fetching keyword search...");
      return keywordSearch(
        _gremlinFetch(connection, featureFlags, options),
        req
      );
    },
    async vertexDetails(req, options) {
      options ??= {};
      options.queryId = v4();

      remoteLogger.info("[Gremlin Explorer] Fetching vertex details...");
      const result = await vertexDetails(
        _gremlinFetch(connection, featureFlags, options),
        req
      );
      return result;
    },
    async edgeDetails(req, options) {
      options ??= {};
      options.queryId = v4();

      remoteLogger.info("[Gremlin Explorer] Fetching edge details...");
      const result = await edgeDetails(
        _gremlinFetch(connection, featureFlags, options),
        req
      );
      return result;
    },
  } satisfies Explorer;
}
